import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Triangle, AlertTriangle, ChevronRight, Lock } from 'lucide-react';
import { format, subDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { useMobileTopNavBar } from 'components/context/MobileTopNavBarContext';
import { useAuth } from 'components/context/AuthContext';
import { useAdsModal } from 'components/context/ModalsContext';
import useHomeSummary from 'hooks/useHomeSummary';
import useHomePendingOptimizations from 'hooks/useHomePendingOptimizations';
import useHomeAccountHealth from 'hooks/useHomeAccountHealth';
import { useObjetivosQuery } from 'queries/objetivos';
import { useGoogleAds } from 'hooks/useGoogleAds';
import { useMetaAds } from 'hooks/useMetaAds';
import PlatformLogo from 'components/ui/PlatformLogo';
import { GoogleIcon, MetaIcon } from 'components/ui/icons';
import NoAdsConnectedSection from 'components/common/NoAdsConnectedSection';
import { formatCurrencyAmount } from 'utils/currency';
import { calcularProgreso, getDesviacionText } from 'utils/objetivosUtils';
import OptimizationDetailModal from 'components/modals/OptimizationDetailModal';
import OptimizationDetailModalV3 from 'components/modals/OptimizationDetailModalV3';
import { flattenKanbanCardsForModalNavigation, isGoogleAdsPlatformUi } from 'utils/optimizationTransformers';

function DeltaTriangleUp(props) {
  return <Triangle {...props} fill="currentColor" stroke="currentColor" strokeWidth={0} />;
}

function DeltaTriangleDown(props) {
  return (
    <Triangle
      {...props}
      fill="currentColor"
      stroke="currentColor"
      strokeWidth={0}
      className={`${props?.className || ''} rotate-180`}
    />
  );
}

function clampNumber(value) {
  if (value === null || value === undefined) return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function formatRoas(value) {
  const n = clampNumber(value);
  if (n === null) return 'N/A';
  return `${n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}x`;
}

function formatConversions(value) {
  const n = clampNumber(value);
  if (n === null) return 'N/A';
  const isInteger = Math.abs(n - Math.round(n)) < 1e-9;
  return n.toLocaleString('es-ES', {
    minimumFractionDigits: isInteger ? 0 : 1,
    maximumFractionDigits: isInteger ? 0 : 1,
  });
}

function formatDeltaPct(pct, { lowerIsBetter = false } = {}) {
  const n = clampNumber(pct);
  if (n === null) return { label: '—', tone: 'neutral', icon: null };

  // n comes in 0.1250 form; show percent.
  const percentage = n * 100;
  const stable = Math.abs(percentage) < 0.05;
  if (stable) {
    return { label: 'estable', tone: 'neutral', icon: null };
  }

  const isPositive = percentage > 0;
  const isGood = lowerIsBetter ? !isPositive : isPositive;

  return {
    label: `${isPositive ? '+' : ''}${percentage.toFixed(1)}%`,
    tone: isGood ? 'good' : 'bad',
    icon: isPositive ? DeltaTriangleUp : DeltaTriangleDown,
  };
}

function parseObjetivoDate(fechaInput) {
  if (!fechaInput) return null;

  if (fechaInput instanceof Date) {
    if (Number.isNaN(fechaInput.getTime())) return null;
    const d = new Date(fechaInput.getTime());
    d.setHours(0, 0, 0, 0);
    return d;
  }

  const fechaStr = String(fechaInput).trim();
  if (!fechaStr) return null;

  // Parse YYYY-MM-DD (or ISO that starts with it) without timezone ambiguity.
  const m = fechaStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);

    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      const fecha = new Date(year, month - 1, day);
      fecha.setHours(0, 0, 0, 0);
      if (fecha.getFullYear() === year && fecha.getMonth() === month - 1 && fecha.getDate() === day) {
        return fecha;
      }
    }
    return null;
  }

  const fecha = new Date(fechaStr);
  if (Number.isNaN(fecha.getTime())) return null;
  fecha.setHours(0, 0, 0, 0);
  return fecha;
}

function clasificarObjetivosPorFechas(objetivos) {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);

  const activos = [];
  const proximos = [];
  const finalizados = [];

  (Array.isArray(objetivos) ? objetivos : []).forEach((objetivo) => {
    // Si no tiene fechas, no lo incluimos en la clasificación (igual que en Objetivos.jsx).
    if (!objetivo?.fecha_inicio && !objetivo?.fecha_fin) return;

    const fechaInicio = parseObjetivoDate(objetivo.fecha_inicio);
    const fechaFin = parseObjetivoDate(objetivo.fecha_fin);

    // Si tiene campos pero no se pudieron parsear, lo mostramos como activo para evitar pérdida.
    if (!fechaInicio && !fechaFin) {
      activos.push(objetivo);
      return;
    }

    if (fechaFin && fechaFin < hoy) {
      finalizados.push(objetivo);
      return;
    }

    if (fechaInicio && fechaInicio > hoy) {
      proximos.push(objetivo);
      return;
    }

    if ((!fechaInicio || fechaInicio <= hoy) && (!fechaFin || fechaFin >= hoy)) {
      activos.push(objetivo);
    }
  });

  return { activos, proximos, finalizados };
}

function GlassCard({ children, className = '' }) {
  return (
    <section
      className={`backdrop-blur-xl bg-gradient-to-br from-slate-800/70 via-slate-900/70 to-indigo-900/70 border border-white/10 rounded-2xl shadow-lg ${className}`}
    >
      {children}
    </section>
  );
}

function SectionHeader({ title, subtitle, actionLabel, onAction }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <h2 className="text-lg font-semibold text-white">{title}</h2>
        {subtitle ? <p className="mt-1 text-sm text-white/70">{subtitle}</p> : null}
      </div>
      {actionLabel && onAction ? (
        <button
          type="button"
          onClick={onAction}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 border border-white/15 text-white text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
        >
          <span>{actionLabel}</span>
          <ChevronRight className="w-4 h-4" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}

function KpiCard({ title, value, delta, tone = 'default' }) {
  const toneStyles =
    tone === 'warn'
      ? 'border-amber-400/20 from-amber-900/30 via-slate-900/70 to-indigo-900/70'
      : 'border-white/10 from-slate-800/70 via-slate-900/70 to-indigo-900/70';

  const deltaColor =
    delta?.tone === 'good'
      ? 'text-emerald-300'
      : delta?.tone === 'bad'
        ? 'text-rose-300'
        : 'text-white/60';

  const DeltaIcon = delta?.icon;

  return (
    <section
      className={`backdrop-blur-xl bg-gradient-to-br ${toneStyles} border rounded-2xl p-5 shadow-card`}
      aria-label={title}
    >
      <div className="text-xs tracking-widest text-white/60 font-semibold uppercase">{title}</div>
      <div className="mt-2 text-2xl sm:text-3xl font-semibold text-white">{value}</div>
      {delta?.label ? (
        <div className={`mt-2 inline-flex items-center gap-1 text-sm font-medium ${deltaColor}`}>
          {DeltaIcon ? <DeltaIcon className="w-4 h-4" aria-hidden="true" /> : null}
          <span>{delta.label}</span>
        </div>
      ) : null}
    </section>
  );
}

function HomeDateRangeHint({ range, compareRange }) {
  const currentLabel = useMemo(() => {
    if (!range?.start || !range?.end) return null;
    const sameYear = range.start.getFullYear() === range.end.getFullYear();
    const startFmt = format(range.start, sameYear ? 'd MMM' : 'd MMM yyyy', { locale: es });
    const endFmt = format(range.end, 'd MMM yyyy', { locale: es });
    return `${startFmt} – ${endFmt}`;
  }, [range?.start, range?.end]);

  const compareLabel = useMemo(() => {
    if (!compareRange?.start || !compareRange?.end) return null;
    const sameYear = compareRange.start.getFullYear() === compareRange.end.getFullYear();
    const startFmt = format(compareRange.start, sameYear ? 'd MMM' : 'd MMM yyyy', { locale: es });
    const endFmt = format(compareRange.end, 'd MMM yyyy', { locale: es });
    return `${startFmt} – ${endFmt}`;
  }, [compareRange?.start, compareRange?.end]);

  if (!currentLabel || !compareLabel) return null;

  return (
    <div className="mb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
      <div className="inline-flex flex-wrap items-center gap-x-2 gap-y-1 text-xs sm:text-sm text-white/70">
        <span className="font-semibold text-white/80">Últimos 7 días:</span>
        <span className="tabular-nums">{currentLabel}</span>
        <span className="text-white/40 hidden sm:inline">·</span>
        <span className="font-semibold text-white/70">Comparado con:</span>
        <span className="tabular-nums">{compareLabel}</span>
      </div>
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <GlassCard className="p-6">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-lg bg-amber-500/15 border border-amber-400/20 p-2">
          <AlertTriangle className="w-5 h-5 text-amber-300" aria-hidden="true" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-white">No pudimos cargar tu resumen</h2>
          <p className="mt-1 text-sm text-white/70">{message}</p>
          <div className="mt-4">
            <button
              type="button"
              onClick={onRetry}
              className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 border border-white/15 text-white text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    </GlassCard>
  );
}

function SkeletonKpis() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 lg:gap-6">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-5 animate-pulse"
        >
          <div className="h-3 w-20 rounded bg-white/10" />
          <div className="mt-3 h-8 w-32 rounded bg-white/10" />
          <div className="mt-3 h-4 w-24 rounded bg-white/10" />
        </div>
      ))}
    </div>
  );
}

function PendingOptimizationsBlock({ loading, error, summary, onRetry, onViewAll, onSelectOptimization, isLockedByObjectives = false, isAdmin = false }) {
  const totalTypes = summary?.totalTypes ?? 0;
  const navigate = useNavigate();
  const showMockLink = isAdmin && process.env.NODE_ENV === 'development';

  const subtitle = isLockedByObjectives
    ? 'Necesitás definir objetivos para desbloquear las optimizaciones.'
    : totalTypes > 0
      ? `Encontramos ${totalTypes} tipos de optimización pendientes`
      : 'Nuestra IA está buscando optimizaciones para tus cuentas. Te avisaremos ni bien estén listas.';

  return (
    <section className="backdrop-blur-xl bg-gradient-to-br from-indigo-900/60 via-slate-900/70 to-fuchsia-900/30 border border-white/15 rounded-2xl shadow-lg p-6">
      <SectionHeader
        title="Optimizaciones"
        subtitle={subtitle}
        actionLabel={totalTypes > 0 && !isLockedByObjectives ? 'Ver todas' : null}
        onAction={totalTypes > 0 && !isLockedByObjectives ? onViewAll : null}
      />
      {showMockLink ? (
        <div className="mt-3 flex items-center justify-between gap-3">
          <div className="text-xs text-white/50">
            Dev: acceso al fixture v2 para diseño.
          </div>
          <button
            type="button"
            onClick={() => navigate('/__mock/optimizations-v2')}
            className="min-h-[44px] inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 border border-white/15 text-white text-xs font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
            title="Abrir mock de optimizaciones v2"
          >
            Abrir mock v2
          </button>
        </div>
      ) : null}

      {loading ? (
        <div className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-white/5 p-4 animate-pulse">
                <div className="h-3 w-24 rounded bg-white/10" />
                <div className="mt-3 h-6 w-28 rounded bg-white/10" />
                <div className="mt-3 h-3 w-20 rounded bg-white/10" />
              </div>
            ))}
          </div>
        </div>
      ) : error ? (
        <div className="mt-4 flex items-center justify-between gap-3">
          <p className="text-sm text-white/70">{error}</p>
          <button
            type="button"
            onClick={onRetry}
            className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 border border-white/15 text-white text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
          >
            Reintentar
          </button>
        </div>
      ) : isLockedByObjectives ? (
        <div className="mt-5">
          <div className="rounded-2xl border border-amber-400/25 bg-amber-500/10 p-4">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-lg bg-amber-500/15 border border-amber-400/20 p-2">
                <Lock className="w-5 h-5 text-amber-300" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-white">Optimizaciones bloqueadas</div>
                <p className="mt-1 text-sm text-white/70">
                  Para desbloquear esta sección, creá objetivos para tu marca.
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : totalTypes === 0 ? null : (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {(summary?.top3 || []).map(({ card, type }) => {
            const platform = String(card?.platform || '').toLowerCase();
            const isGoogle = platform.includes('google');
            const isMeta = platform.includes('meta');
            const typeUpper = String(type || card?.type || '').toUpperCase();
            const emoji =
              typeUpper.includes('PAUS') ? '⏸️' : typeUpper.includes('ESCAL') ? '🚀' : '✨';

            return (
            <button
              key={card.id}
              type="button"
              onClick={() => (typeof onSelectOptimization === 'function' ? onSelectOptimization(card) : onViewAll())}
              className="text-left rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
              title="Ver detalles de la optimización"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-lg" aria-hidden="true">
                    {emoji}
                  </span>
                  <div className="min-w-0">
                    <div className="text-xs tracking-widest text-white/60 font-semibold uppercase">
                      {typeUpper ? typeUpper.toLowerCase() : 'optimización'}
                    </div>
                    <div className="mt-1 text-base font-semibold text-white line-clamp-2">{card.action}</div>
                  </div>
                </div>
                {(isGoogle || isMeta) && (
                  <span className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-white/15 bg-white/10 text-xs font-semibold text-white/80">
                    {isGoogle ? <GoogleIcon width={16} height={16} /> : null}
                    {isMeta ? <MetaIcon width={16} height={16} /> : null}
                    <span>{isGoogle ? 'Google Ads' : 'Meta Ads'}</span>
                  </span>
                )}
              </div>
            </button>
          );
          })}
        </div>
      )}
    </section>
  );
}

function HealthCheckRow({ title, value, badge, onBadgeClick }) {
  const badgeStyles =
    badge?.tone === 'good'
      ? 'bg-emerald-500/15 text-emerald-200 border-emerald-400/20'
      : badge?.tone === 'bad'
        ? 'bg-rose-500/15 text-rose-200 border-rose-400/20'
        : badge?.tone === 'warn'
          ? 'bg-amber-500/15 text-amber-200 border-amber-400/20'
          : 'bg-white/10 text-white/70 border-white/15';

  const Chip = onBadgeClick ? 'button' : 'span';

  return (
    <div className="flex items-center justify-between gap-4 py-3">
      <div className="min-w-0">
        <div className="text-sm font-medium text-white">{title}</div>
        {value ? <div className="mt-0.5 text-xs text-white/60">{value}</div> : null}
      </div>
      <Chip
        type={onBadgeClick ? 'button' : undefined}
        onClick={onBadgeClick || undefined}
        className={`shrink-0 px-2.5 py-1 rounded-lg border text-xs font-semibold ${badgeStyles} ${
          onBadgeClick
            ? 'hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40'
            : ''
        }`}
      >
        {badge?.label || 'No disponible'}
      </Chip>
    </div>
  );
}

function AccountHealthBlock({ checks, loading, onNavigateAdsAge }) {
  const nav = onNavigateAdsAge || null;

  const loadingBadge = { label: 'Cargando', tone: 'neutral', value: null };
  const adSpendConcentration = loading ? loadingBadge : checks?.adSpendConcentration;
  const activeAds = loading ? loadingBadge : checks?.activeAds;
  const adsAge = loading ? loadingBadge : checks?.adsAge;
  const campaignConcentration = loading ? loadingBadge : checks?.campaignConcentration;

  return (
    <GlassCard className="p-6 h-full flex flex-col">
      <SectionHeader title="Salud de la cuenta" />
      <div className="mt-4 divide-y divide-white/10 flex-1">
        <HealthCheckRow
          title="Concentración de inversión (por anuncio)"
          value={adSpendConcentration?.value || null}
          badge={adSpendConcentration}
          onBadgeClick={nav}
        />
        <HealthCheckRow
          title="Anuncios activos"
          value={activeAds?.value || null}
          badge={activeAds}
          onBadgeClick={nav}
        />
        <HealthCheckRow
          title="Edad de anuncios"
          value={adsAge?.value || null}
          badge={adsAge}
          onBadgeClick={nav}
        />
        <HealthCheckRow
          title="Concentración de campaña"
          value={campaignConcentration?.value || null}
          badge={campaignConcentration}
          onBadgeClick={nav}
        />
      </div>
    </GlassCard>
  );
}

function ObjectivesHomeBlock({ loading, objectives, onViewAll, currencyFallback = 'USD' }) {
  const { activos, proximos } = useMemo(
    () => clasificarObjetivosPorFechas(objectives),
    [objectives]
  );

  const source = activos.length > 0 ? activos : proximos;
  const top = source.slice(0, 3);

  const formatTarget = useCallback((valor, tipo, currency = null) => {
    if (valor === null || valor === undefined || valor === '' || Number.isNaN(Number(valor))) return 'N/A';
    const n = Number(valor);
    const metrica = String(tipo || '');
    if (metrica.toUpperCase().includes('ROAS')) return `${n.toFixed(2)}x`;
    if (metrica.toUpperCase().includes('CONVERSION')) return formatConversions(n);
    if (metrica === 'CPA') {
      return formatCurrencyAmount(n, currency || currencyFallback, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    // Presupuesto/Inversión: sin decimales para que quede lo más compacto posible.
    if (metrica === 'Presupuesto') {
      return formatCurrencyAmount(n, currency || currencyFallback, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    }
    return String(valor);
  }, [currencyFallback]);

  const computeFillPct = useCallback((objetivo) => {
    const actual = Number(objetivo?.valor_actual ?? 0);
    const target = Number(objetivo?.target ?? 0);
    if (!Number.isFinite(actual) || !Number.isFinite(target) || target === 0) return 0;
    const metrica = objetivo?.metrica_relacionada;

    // CPA: menor es mejor. Si está pasado (actual > target) => barra completa.
    if (metrica === 'CPA') {
      const pct = (actual / target) * 100;
      return Math.min(100, Math.max(0, pct));
    }

    // Resto: progreso normal (mayor es mejor).
    return Math.min(100, Math.max(0, calcularProgreso(actual, target)));
  }, []);

  return (
    <GlassCard className="p-6 h-full flex flex-col">
      <SectionHeader
        title="Objetivos"
        subtitle={activos.length > 0 ? 'Progreso de objetivos activos' : proximos.length > 0 ? 'Próximos objetivos' : null}
        actionLabel="Ver todos"
        onAction={onViewAll}
      />

      {loading ? (
        <div className="mt-4 space-y-3 flex-1">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-16 rounded-xl bg-white/5 border border-white/10 animate-pulse" />
          ))}
        </div>
      ) : top.length === 0 ? (
        <div className="mt-4 flex-1">
          <div className="rounded-2xl border border-amber-400/25 bg-amber-500/10 p-4">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-lg bg-amber-500/15 border border-amber-400/20 p-2">
                <Lock className="w-5 h-5 text-amber-300" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-white">Sin objetivos activos</div>
                <p className="mt-1 text-sm text-white/70">
                  Creá objetivos con fechas para ver su progreso en el inicio.
                </p>
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={onViewAll}
                    className="inline-flex items-center gap-2 backdrop-blur-xl bg-gradient-to-r from-cyan-500/20 via-teal-500/20 to-cyan-600/20 border border-cyan-400/30 rounded-full px-5 py-2.5 shadow-[0_4px_14px_-2px_rgba(6,182,212,0.3),0_4px_20px_-4px_rgba(20,184,166,0.4)] hover:border-cyan-400/50 hover:scale-105 active:scale-[0.98] transition-all duration-300 text-white/90 text-sm font-medium"
                  >
                    Crear objetivos
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-4 divide-y divide-white/10 flex-1">
          {top.map((o) => {
            const actualStr = formatTarget(o?.valor_actual, o?.metrica_relacionada, o?.currency);
            const targetStr = formatTarget(o?.target, o?.metrica_relacionada, o?.currency);
            const desviacion = getDesviacionText(o); // solo para color de barra
            const fill = computeFillPct(o);
            const barColor =
              desviacion?.color?.includes('green')
                ? 'from-emerald-500 to-emerald-400'
                : 'from-amber-500 to-amber-400';
            const chipTextColor = desviacion?.color?.includes('green') ? 'text-emerald-200' : 'text-amber-200';

            return (
              <button
                key={o.id}
                type="button"
                onClick={onViewAll}
                className="w-full text-left py-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 rounded-lg"
                title="Ver objetivos"
              >
                <div className="flex items-center gap-3">
                  <div className="shrink-0 w-7 flex items-center justify-center self-stretch">
                    <PlatformLogo plataformas={o?.plataformas_relacionadas} />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-semibold text-white truncate">{o?.nombre || 'Objetivo'}</div>
                      <span className={`shrink-0 px-2.5 py-1 rounded-lg text-xs font-semibold bg-white/10 tabular-nums ${chipTextColor}`}>
                        {actualStr} / {targetStr}
                      </span>
                    </div>

                    <div className="mt-3 h-2 rounded-full bg-white/10 overflow-hidden" aria-hidden="true">
                      <div className={`h-full bg-gradient-to-r ${barColor}`} style={{ width: `${fill}%` }} />
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </GlassCard>
  );
}

const Home = () => {
  const { setTopNavBarConfig, resetConfig } = useMobileTopNavBar();
  const { auth } = useAuth();
  const isAdmin = Boolean(auth?.user?.is_admin);
  const navigate = useNavigate();
  const { setIsOptimizationDetailModalOpen: setGlobalOptimizationDetailModalOpen } = useAdsModal();
  const { connectGoogleAds } = useGoogleAds();
  const { startMetaOAuth } = useMetaAds();
  const activeBrandId = auth?.user?.active_brand_id;
  const { loading, error, data, refetch } = useHomeSummary();
  const {
    loading: loadingPending,
    error: pendingError,
    summary: pendingSummary,
    groupCards: pendingGroupCards,
    refetch: refetchPending,
  } =
    useHomePendingOptimizations({ enabled: Boolean(activeBrandId) });

  const { data: objetivos = [], isPending: loadingObjetivos } = useObjetivosQuery(
    activeBrandId != null ? String(activeBrandId) : null
  );

  const health = useHomeAccountHealth({ homeSummary: data });

  const hasGoogleConnected = Boolean(auth?.user?.google_ads_connected && auth?.user?.google_ads_customer_id);
  const hasMetaConnected = Boolean(auth?.user?.meta_connected && auth?.user?.meta_ad_account_id);
  const hasAnyConnection = hasGoogleConnected || hasMetaConnected;
  const hasAnyActiveObjectives = useMemo(() => {
    const { activos } = clasificarObjetivosPorFechas(objetivos);
    return activos.length > 0;
  }, [objetivos]);

  const [selectedOptimization, setSelectedOptimization] = useState(null);
  const [currentOptimizationIndex, setCurrentOptimizationIndex] = useState(0);
  const [isOptimizationModalOpen, setIsOptimizationModalOpen] = useState(false);
  const [currentGroupIndex, setCurrentGroupIndex] = useState(0);

  // Google se queda con la tarjeta legacy (la que ven los no-admin en producción);
  // el rediseño v3 aplica solo a Meta.
  const isGoogleSelected = isGoogleAdsPlatformUi(selectedOptimization?.platform ?? selectedOptimization?._raw?.platform);
  const DetailModalComponent = (isAdmin && !isGoogleSelected) ? OptimizationDetailModalV3 : OptimizationDetailModal;

  const optimizationsForDetailModal = useMemo(
    () => flattenKanbanCardsForModalNavigation(pendingGroupCards),
    [pendingGroupCards]
  );

  const allGroupsForDetailModal = useMemo(() => {
    const groupCards = Array.isArray(pendingGroupCards) ? pendingGroupCards : [];
    return groupCards
      .map((g) => ({
        ruleKey: g?.rule_key ?? null,
        cards: Array.isArray(g?.childCards) ? g.childCards : [],
      }))
      .filter((g) => Array.isArray(g.cards) && g.cards.length > 0);
  }, [pendingGroupCards]);

  const handleOpenOptimizationFromHome = useCallback(
    (groupCard) => {
      const groupCards = Array.isArray(pendingGroupCards) ? pendingGroupCards : [];
      const groupIdx = groupCards.findIndex((c) => c?.id === groupCard?.id);
      const safeGroupIndex = groupIdx >= 0 ? groupIdx : 0;
      const representative =
        Array.isArray(groupCard?.childCards) && groupCard.childCards.length > 0
          ? groupCard.childCards[0]
          : groupCard;
      const flat = flattenKanbanCardsForModalNavigation(groupCards);
      const index = flat.findIndex((c) => c?.id === representative?.id);

      setCurrentGroupIndex(safeGroupIndex);
      setSelectedOptimization(representative || null);
      setCurrentOptimizationIndex(index >= 0 ? index : 0);
      setGlobalOptimizationDetailModalOpen(true);
      setIsOptimizationModalOpen(true);
    },
    [pendingGroupCards, setGlobalOptimizationDetailModalOpen]
  );

  const handleNavigateGroup = useCallback(
    (newGroupIndex) => {
      if (newGroupIndex < 0 || newGroupIndex >= allGroupsForDetailModal.length) return;
      setCurrentGroupIndex(newGroupIndex);
      const groupCards = allGroupsForDetailModal[newGroupIndex]?.cards;
      if (groupCards && groupCards[0]) {
        setSelectedOptimization(groupCards[0]);
        setCurrentOptimizationIndex(0);
      }
    },
    [allGroupsForDetailModal]
  );

  useEffect(() => {
    setTopNavBarConfig({
      hideFilters: true,
      hideBrandSelector: false,
      hideBack: true,
    });
    return () => resetConfig();
  }, [setTopNavBarConfig, resetConfig]);

  const combined = data?.buckets?.current?.combined || null;
  const displayCurrency = data?.display_currency || combined?.currency || 'USD';

  const homeRanges = useMemo(() => {
    // Normalizar a mediodía local para evitar corrimientos por timezone.
    const today = new Date();
    today.setHours(12, 0, 0, 0);

    // Últimos 7 días completos: del día -7 al día -1 (ayer).
    const currentEnd = subDays(today, 1);
    const currentStart = subDays(today, 7);

    // Comparativa: 7 días anteriores.
    const previousEnd = subDays(today, 8);
    const previousStart = subDays(today, 14);

    return {
      current: { start: currentStart, end: currentEnd },
      previous: { start: previousStart, end: previousEnd },
    };
  }, []);

  const kpis = useMemo(() => {
    const deltas = data?.deltas || {};
    const roas = combined?.roas;
    const cpa = combined?.cpa;
    const cpc = combined?.cpc;
    const investment = combined?.investment;
    const conversions = combined?.conversions;

    const hasRoas = roas !== null && roas !== undefined;
    const hasCpa = cpa !== null && cpa !== undefined;

    const investmentCard = {
      key: 'investment',
      title: 'Inversión',
      value: formatCurrencyAmount(investment, displayCurrency, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      delta: formatDeltaPct(deltas?.investment_pct),
    };

    const conversionsCard = {
      key: 'conversions',
      title: 'Conversiones',
      value: formatConversions(conversions),
      delta: formatDeltaPct(deltas?.conversions_pct),
    };

    const roasCard = {
      key: 'roas',
      title: 'ROAS',
      value: formatRoas(roas),
      delta: formatDeltaPct(deltas?.roas_pct),
    };

    const cpaCard = {
      key: 'cpa',
      title: 'CPA',
      value: formatCurrencyAmount(cpa, displayCurrency, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      delta: formatDeltaPct(deltas?.cpa_pct, { lowerIsBetter: true }),
      tone: 'warn',
    };

    const cpcCard = {
      key: 'cpc',
      title: 'CPC',
      value: formatCurrencyAmount(cpc, displayCurrency, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      delta: formatDeltaPct(deltas?.cpc_pct, { lowerIsBetter: true }),
    };

    if (hasRoas) {
      return [investmentCard, roasCard, conversionsCard, ...(hasCpa ? [cpaCard] : [])];
    }

    if (!hasRoas && hasCpa) {
      return [investmentCard, cpaCard, conversionsCard, cpcCard];
    }

    // Fallback: keep 3 cards when ROAS/CPA are missing.
    return [investmentCard, conversionsCard, cpcCard];
  }, [combined, data?.deltas, displayCurrency]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        {!activeBrandId ? (
          <GlassCard className="p-6">
            <h2 className="text-lg font-semibold text-white">Seleccioná una marca para ver el resumen</h2>
            <p className="mt-1 text-sm text-white/70">
              Usá el selector de marcas para elegir con qué cuenta querés trabajar.
            </p>
          </GlassCard>
        ) : !hasAnyConnection ? (
          <div className="w-full">
            <NoAdsConnectedSection
              connectGoogleAds={connectGoogleAds}
              startMetaOAuth={startMetaOAuth}
              activePlatform="all"
              activeGoogleAccount={null}
              activeMetaAccount={null}
              context="analytics"
            />
          </div>
        ) : error ? (
          <ErrorState message={error} onRetry={refetch} />
        ) : (
          <>
            <HomeDateRangeHint range={homeRanges.current} compareRange={homeRanges.previous} />
            {loading ? (
              <SkeletonKpis />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 lg:gap-6">
                {kpis.map((k) => (
                  <KpiCard key={k.key} title={k.title} value={k.value} delta={k.delta} tone={k.tone} />
                ))}
              </div>
            )}

            <div className="mt-6 grid grid-cols-12 gap-4 lg:gap-6">
              <div className="col-span-12">
                <PendingOptimizationsBlock
                  loading={loadingPending}
                  error={pendingError}
                  summary={pendingSummary}
                  onRetry={refetchPending}
                  onViewAll={() => navigate('/optimizador')}
                  onSelectOptimization={handleOpenOptimizationFromHome}
                  isLockedByObjectives={!hasAnyActiveObjectives}
                  isAdmin={Boolean(auth?.user?.is_admin)}
                />
              </div>
              <div className="col-span-12 lg:col-span-7">
                {!hasMetaConnected ? (
                  <GlassCard className="p-6 h-full flex flex-col">
                    <SectionHeader title="Salud de la cuenta" />
                    <div className="mt-4 flex-1 rounded-2xl border border-amber-400/25 bg-amber-500/10 p-4">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 rounded-lg bg-amber-500/15 border border-amber-400/20 p-2">
                          <Lock className="w-5 h-5 text-amber-300" aria-hidden="true" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-white">Sección bloqueada</div>
                          <p className="mt-1 text-sm text-white/70">
                            Conectá tu cuenta de Meta para desbloquear esta sección.
                          </p>
                          <div className="mt-4">
                            <button
                              type="button"
                              onClick={() => startMetaOAuth(false)}
                              className="inline-flex items-center gap-2 backdrop-blur-xl bg-gradient-to-r from-blue-600 to-purple-600 border border-white/15 rounded-full px-5 py-2.5 shadow-lg hover:scale-105 active:scale-[0.98] transition-all duration-300 text-white text-sm font-semibold"
                            >
                              <MetaIcon width={18} height={18} />
                              Conectar Meta Ads
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </GlassCard>
                ) : (
                  <AccountHealthBlock
                    checks={health.checks}
                    loading={loading}
                    onNavigateAdsAge={() => navigate('/ads-age')}
                  />
                )}
              </div>
              <div className="col-span-12 lg:col-span-5">
                <ObjectivesHomeBlock
                  loading={loadingObjetivos}
                  objectives={objetivos}
                  onViewAll={() => navigate('/objetivos')}
                  currencyFallback={displayCurrency}
                />
              </div>
            </div>
          </>
        )}
      </div>

      <DetailModalComponent
        isOpen={isOptimizationModalOpen}
        onClose={() => {
          setGlobalOptimizationDetailModalOpen(false);
          setIsOptimizationModalOpen(false);
          setSelectedOptimization(null);
          setCurrentOptimizationIndex(0);
          setCurrentGroupIndex(0);
        }}
        optimization={selectedOptimization}
        allOptimizations={optimizationsForDetailModal}
        currentIndex={currentOptimizationIndex}
        onNavigate={() => {}}
        onRatingSuccess={() => {}}
        onApplySuccess={() => {}}
        allGroups={allGroupsForDetailModal}
        currentGroupIndex={currentGroupIndex}
        onNavigateGroup={handleNavigateGroup}
      />
    </div>
  );
};

export default Home;
